import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Container, Row, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, InputGroup, InputGroupText } from 'reactstrap';
import AdminTablePagination from '../../../Components/Common/AdminTablePagination';
import {
  getAdminBattleCollection,
  createAdminBattle,
  cancelAdminBattle,
  getAdminBattleVideoUploadUrl,
  AdminBattleVideoUploadURLResponse,
  AdminBattleItem,
} from '../../../helpers/api/admin/battle';
import { getCharacters } from '../../../helpers/api/admin/character';
import { uploadVideoToCloudflare } from '../../../helpers/cloudflare';
import { formatYmdHm, isValidYmdHm, toIsoUtcFromYmdHm } from '../../../helpers/date';

type BattleItem = AdminBattleItem;

export const DEFAULT_AUTOMATIC_DISTRIBUTION_DELAY = "60";

export const getDefaultBattleStartTime = () => {
  return formatYmdHm(new Date(Date.now() + 60 * 60 * 1000));
};

const Battle = () => {
  document.title = 'Admin Colosseum | Battle management';

  const [data, setData] = useState<BattleItem[]>([]);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<BattleItem | null>(null);
  const [cancelingBattleId, setCancelingBattleId] = useState<string | number | null>(null);

  // Create battle modal state
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [characters, setCharacters] = useState<Array<{ id: string; name: string }>>([]);
  const [formTitle, setFormTitle] = useState<string>("");
  const [formStart, setFormStart] = useState<string>(getDefaultBattleStartTime()); // UI format: YYYY-MM-DD HH:MM
  const [automaticDistributionDelay, setAutomaticDistributionDelay] = useState<string>(DEFAULT_AUTOMATIC_DISTRIBUTION_DELAY);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([]);
  const [winnerId, setWinnerId] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);

  // UI state for 2-line participant selection interface
  const [rowSelect1, setRowSelect1] = useState<string>("");
  const [rowSelect2, setRowSelect2] = useState<string>("");
  const [participant1Id, setParticipant1Id] = useState<string>("");
  const [participant2Id, setParticipant2Id] = useState<string>("");

  // Step 2: optional video upload
  const [step, setStep] = useState<1 | 2>(1);
  const [, setCreatedBattleId] = useState<string | number | null>(null);
  const [uploadInfo, setUploadInfo] = useState<AdminBattleVideoUploadURLResponse | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const totalPages = useMemo(() => {
    if (typeof total === 'number' && total > 0) return Math.max(1, Math.ceil(total / pageSize));
    return 1;
  }, [total, pageSize]);
  const displayedTotal = typeof total === 'number' ? total : data.length;
  const fromRow = displayedTotal > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const toRow = displayedTotal > 0 ? Math.min(fromRow + data.length - 1, displayedTotal) : 0;

  const formatDateTime = (value?: string | number | Date) => {
    return formatYmdHm(value ?? null);
  };

  const isActiveBattle = (battle: BattleItem) => String(battle.status ?? "").toLowerCase() === "active";

  const fetchBattles = async (page: number, limit: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminBattleCollection({ page, limit });

      setData(res.items);
      setTotal(res.meta.totalItems);
    } catch (e: any) {
      setError(typeof e === 'string' ? e : e?.message || 'Failed to load battles');
      setData([]);
      setTotal(undefined);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormTitle("");
    setFormStart(getDefaultBattleStartTime());
    setAutomaticDistributionDelay(DEFAULT_AUTOMATIC_DISTRIBUTION_DELAY);
    setSelectedCharacterIds([]);
    setWinnerId("");
    setFormError(null);
    setRowSelect1("");
    setRowSelect2("");
    setParticipant1Id("");
    setParticipant2Id("");
    // Step 2 state
    setStep(1);
    setCreatedBattleId(null);
    setUploadInfo(null);
    setSelectedFile(null);
    setIsDragging(false);
    setUploading(false);
    setUploadError(null);
  };

  const openCreateModal = async () => {
    setFormError(null);
    setUploadError(null);
    setStep(1);
    setIsCreateOpen(true);
    try {
      const response = await getCharacters({ limit: 50, sort: 'name', order: 'ASC' });
      setCharacters(response.items);
    } catch (e: any) {
      setFormError(typeof e === 'string' ? e : e?.message || 'Failed to load characters');
      setCharacters([]);
    }
  };

  const closeCreateModal = () => {
    setIsCreateOpen(false);
    resetForm();
  };

  const getCharId = (c: { id: string }) => c.id;
  const getCharLabel = (c?: { name: string }) => c?.name ?? "";

  // Keep selectedCharacterIds in sync with the two participant slots
  useEffect(() => {
    const ids = [participant1Id, participant2Id].filter(Boolean) as string[];
    setSelectedCharacterIds(ids);
    if (winnerId && !ids.includes(winnerId)) {
      setWinnerId("");
    }
  }, [participant1Id, participant2Id, winnerId]);

  const handleAddParticipant = (row: 1 | 2) => {
    setFormError(null);
    const chosen = row === 1 ? rowSelect1 : rowSelect2;
    if (!chosen) return;
    // prevent selecting the same character in both rows
    const other = row === 1 ? participant2Id : participant1Id;
    if (other && other === chosen) {
      setFormError('This character is already selected in the other row');
      return;
    }
    if (row === 1) {
      setParticipant1Id(chosen);
    } else {
      setParticipant2Id(chosen);
    }
  };

  const handleAutomaticDistributionDelayChange = (value: string) => {
    setAutomaticDistributionDelay(value.replace(/\D/g, ""));
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formTitle.trim()) {
      setFormError('Title is required');
      return;
    }
    if (!formStart) {
      setFormError('Start time is required');
      return;
    }
    if (!automaticDistributionDelay) {
      setFormError('Automatic distribution delay is required');
      return;
    }
    if (selectedCharacterIds.length < 2) {
      setFormError('Please select at least two characters');
      return;
    }
    if (!winnerId) {
      setFormError('Please select a winner');
      return;
    }
    if (!selectedCharacterIds.includes(winnerId)) {
      setFormError('Selected winner must be among participants');
      return;
    }

    if (!isValidYmdHm(formStart)) {
      setFormError('Start time must be in format YYYY-MM-DD HH:MM');
      return;
    }

    let isoStart: string;
    try {
      isoStart = toIsoUtcFromYmdHm(formStart);
    } catch (_) {
      setFormError('Invalid start time');
      return;
    }

    const participations = selectedCharacterIds.map(cid => ({
      characterId: cid,
      isWinner: cid === winnerId,
    }));

    const payload = {
      title: formTitle.trim(),
      startTime: isoStart,
      automaticDistributionDelay: Number(automaticDistributionDelay),
      participations,
    };

    try {
      setCreating(true);
      const created = await createAdminBattle(payload as any);
      const battleId = created.id;
      if (!battleId) {
        // If id missing, proceed to close as before
        closeCreateModal();
        fetchBattles(currentPage, pageSize);
        return;
      }
      setCreatedBattleId(battleId);
      try {
        const info = await getAdminBattleVideoUploadUrl(String(battleId));
        setUploadInfo(info);
      } catch (err: any) {
        // If we can't get upload URL, just move to finish behavior (no video)
        closeCreateModal();
        fetchBattles(currentPage, pageSize);
        return;
      }
      // Switch to step 2 for optional upload
      setStep(2);
    } catch (e: any) {
      setFormError(typeof e === 'string' ? e : e?.message || 'Failed to create battle');
    } finally {
      setCreating(false);
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type || !file.type.startsWith('video/')) {
      setUploadError('Only video files are accepted.');
      e.currentTarget.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const onDropFile = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setUploadError(null);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    if (!file.type || !file.type.startsWith('video/')) {
      setUploadError('Only video files are accepted.');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!uploadInfo || !uploadInfo.uploadURL || !selectedFile) return;
    try {
      setUploading(true);
      setUploadError(null);
      await uploadVideoToCloudflare(selectedFile, uploadInfo.uploadURL);
      // After successful upload, close modal and refresh
      closeCreateModal();
      fetchBattles(currentPage, pageSize);
    } catch (e: any) {
      setUploadError(typeof e === 'string' ? e : e?.message || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleSkipUpload = () => {
    // Just close and refresh
    closeCreateModal();
    fetchBattles(currentPage, pageSize);
  };

  const handleCancelBattle = async () => {
    if (!cancelTarget?.id) return;
    try {
      setCancelingBattleId(cancelTarget.id);
      setError(null);
      await cancelAdminBattle(cancelTarget.id);
      setCancelTarget(null);
      fetchBattles(currentPage, pageSize);
    } catch (e: any) {
      setError(typeof e === 'string' ? e : e?.message || 'Failed to cancel battle');
    } finally {
      setCancelingBattleId(null);
    }
  };
 
  useEffect(() => {
    fetchBattles(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  return (
    <React.Fragment>
      <div className="page-content admin-users-page">
        <Container fluid>
          <Row className="mb-3">
            <Col xs={12} xl={12}>
              <h4 className="mb-0">Battles</h4>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="page-toolbar d-flex align-items-center justify-content-end gap-3 mb-3">
                <div>
                  <Button color="primary" onClick={openCreateModal}>Create battle</Button>
                </div>
              </div>

              <div className="admin-users-panel">
                <div className="admin-users-table-wrap">
                  <table className="admin-users-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Start Time</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Betting Type</th>
                        <th>Created At</th>
                        <th>Updated At</th>
                        <th aria-label="Actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={8} className="text-center">
                            <Spinner size="sm" className="me-2" /> Loading battles...
                          </td>
                        </tr>
                      ) : data && data.length > 0 ? (
                        data.map((item, idx) => (
                          <tr key={item.id ?? idx}>
                            <td>{item.title ?? '-'}</td>
                            <td>{formatDateTime(item.startTime)}</td>
                            <td>{item.type ?? '-'}</td>
                            <td>{item.status ?? '-'}</td>
                            <td>{item.bettingType ?? '-'}</td>
                            <td>{formatDateTime(item.createdAt)}</td>
                            <td>{formatDateTime(item.updatedAt)}</td>
                            <td className="text-end">
                              {item.id ? (
                                <div className="battle-row-actions">
                                  <Button
                                    tag={Link as any}
                                    color="primary"
                                    size="sm"
                                    className="battle-bets-action"
                                    to={`/admin/battles/${item.id}/bets`}
                                    state={{ battle: item }}
                                    data-cy="battle-bets-link"
                                  >
                                    <i className="ri-coins-line" aria-hidden="true" />
                                    <span>Bets</span>
                                  </Button>
                                  {isActiveBattle(item) && (
                                    <Button
                                      color="danger"
                                      size="sm"
                                      className="battle-cancel-action"
                                      aria-label={`Cancel battle ${item.title ?? item.id}`}
                                      disabled={cancelingBattleId === item.id}
                                      onClick={() => setCancelTarget(item)}
                                      data-cy="battle-cancel-button"
                                    >
                                      {cancelingBattleId === item.id ? (
                                        <Spinner size="sm" aria-hidden="true" />
                                      ) : (
                                        <i className="ri-close-circle-line" aria-hidden="true" />
                                      )}
                                      <span>Cancel</span>
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">No battles found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="admin-users-footer">
                  <div className="admin-users-page-size">
                    <label htmlFor="pageSize">Rows</label>
                    <select
                      id="pageSize"
                      className="form-select"
                      data-cy="admin-battles-page-size"
                      value={pageSize}
                      onChange={(e) => {
                        setCurrentPage(1);
                        setPageSize(parseInt(e.target.value, 10));
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span data-cy="admin-battles-pagination-summary">
                      Showing {fromRow}-{toRow} of {displayedTotal} battles
                    </span>
                  </div>
                  <AdminTablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    disabled={loading}
                    onPageChange={setCurrentPage}
                    nextDataCy="admin-battles-next-page"
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        <Modal isOpen={isCreateOpen} toggle={closeCreateModal} size="lg">
          <ModalHeader toggle={closeCreateModal}>{step === 1 ? 'Create battle' : 'Upload battle video (optional)'}</ModalHeader>

          {step === 1 ? (
            <Form onSubmit={handleSubmitCreate}>
              <ModalBody>
                {formError && (
                  <div className="alert alert-danger" role="alert">{formError}</div>
                )}
                <Row className="g-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label for="battleTitle">Title</Label>
                      <Input
                        id="battleTitle"
                        type="text"
                        value={formTitle}
                        onChange={(e) => setFormTitle(e.target.value)}
                        placeholder="Enter title"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={5}>
                    <FormGroup>
                      <Label for="battleStart">Start time (YYYY-MM-DD HH:MM)</Label>
                      <Input
                        id="battleStart"
                        type="text"
                        placeholder="e.g., 2025-11-30 14:45"
                        value={formStart}
                        onChange={(e) => setFormStart(e.target.value)}
                        aria-invalid={!!formStart && !isValidYmdHm(formStart)}
                        data-cy="battle-start-time"
                        required
                      />
                      <div className="form-text">Use format YYYY-MM-DD HH:MM (24-hour, local time)</div>
                      {!!formStart && !isValidYmdHm(formStart) && (
                        <div className="text-danger small">Invalid format. Example: 2025-11-30 14:45</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="automaticDistributionDelay">Automatic distribution delay</Label>
                      <InputGroup>
                        <Input
                          id="automaticDistributionDelay"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={automaticDistributionDelay}
                          onChange={(e) => handleAutomaticDistributionDelayChange(e.target.value)}
                          aria-label="Automatic distribution delay in minutes"
                          data-cy="battle-automatic-distribution-delay"
                          required
                        />
                        <InputGroupText>min</InputGroupText>
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>

                <hr />
                {/* Label */}
                <Row className="g-3 align-items-end mb-2">
                  <Col md={4}>
                    <Label>Characters</Label>
                  </Col>
                  <Col md={2}>
                  </Col>
                  <Col md={4}>
                    <Label>Participants</Label>
                  </Col>
                  <Col md={2}>
                    <Label>Winner</Label>
                  </Col>
                </Row>
                {/* Ligne 1 */}
                <Row className="g-3 align-items-end mb-2">
                  <Col md={4}>
                    <FormGroup>
                      <Input
                        id="row1-select"
                        type="select"
                        value={rowSelect1}
                        onChange={(e) => setRowSelect1(e.target.value)}
                      >
                        <option value="">-- Select --</option>
                        {characters.map((c: any) => {
                          const id = getCharId(c);
                          const label = getCharLabel(c);
                          return (
                            <option key={id} value={id}>{label}</option>
                          );
                        })}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup className="mb-2">
                      <Button
                        color="primary"
                        className="w-100"
                        type="button"
                        onClick={() => handleAddParticipant(1)}
                        disabled={!rowSelect1 || rowSelect1 === participant2Id}
                      >
                        Add
                      </Button>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup className="mb-2">
                      <Input
                        type="text"
                        value={participant1Id ? getCharLabel(characters.find((c: any) => getCharId(c) === participant1Id)) : ''}
                        readOnly
                        placeholder="No participant selected"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <div className="form-check">
                        <Input
                          className="form-check-input"
                          type="radio"
                          id="winner1"
                          name="winner"
                          disabled={!participant1Id}
                          checked={!!participant1Id && winnerId === participant1Id}
                          onChange={() => setWinnerId(participant1Id)}
                        />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Ligne 2 */}
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <FormGroup>
                      <Input
                        id="row2-select"
                        type="select"
                        value={rowSelect2}
                        onChange={(e) => setRowSelect2(e.target.value)}
                      >
                        <option value="">-- Select --</option>
                        {characters.map((c: any) => {
                          const id = getCharId(c);
                          const label = getCharLabel(c);
                          return (
                            <option key={id} value={id}>{label}</option>
                          );
                        })}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup className="mb-2">
                      <Button
                        color="primary"
                        className="w-100"
                        type="button"
                        onClick={() => handleAddParticipant(2)}
                        disabled={!rowSelect2 || rowSelect2 === participant1Id}
                      >
                        Add
                      </Button>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup className="mb-2">
                      <Input
                        type="text"
                        value={participant2Id ? getCharLabel(characters.find((c: any) => getCharId(c) === participant2Id)) : ''}
                        readOnly
                        placeholder="No participant selected"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <div className="form-check">
                        <Input
                          className="form-check-input"
                          type="radio"
                          id="winner2"
                          name="winner"
                          disabled={!participant2Id}
                          checked={!!participant2Id && winnerId === participant2Id}
                          onChange={() => setWinnerId(participant2Id)}
                        />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={closeCreateModal} disabled={creating}>Cancel</Button>
                <Button color="primary" type="submit" disabled={creating}>
                  {creating ? (<><Spinner size="sm" className="me-2" /> Creating...</>) : 'Create'}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            <>
              <ModalBody>
                {uploadError && (
                  <div className="alert alert-danger" role="alert">{uploadError}</div>
                )}
                <div
                  onDrop={onDropFile}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  className={`border rounded p-5 text-center ${isDragging ? 'bg-light' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <p className="mb-2">Drag & drop a video here, or click to browse</p>
                  <p className="text-muted">Accepted formats: video/*</p>
                  {selectedFile && (
                    <div className="mt-3">
                      <strong>Selected file:</strong> {selectedFile.name}
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept="video/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={onPickFile}
                />
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={handleSkipUpload} disabled={uploading}>Skip</Button>
                <Button color="primary" onClick={handleUpload} disabled={uploading || !selectedFile}>
                  {uploading ? (<><Spinner size="sm" className="me-2" /> Uploading...</>) : 'Upload'}
                </Button>
              </ModalFooter>
            </>
          )}
        </Modal>

        <Modal isOpen={!!cancelTarget} toggle={() => setCancelTarget(null)} centered fade={false}>
          <ModalHeader toggle={() => setCancelTarget(null)}>Cancel battle</ModalHeader>
          <ModalBody>
            Are you sure you want to cancel <strong>{cancelTarget?.title ?? 'this battle'}</strong>?
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setCancelTarget(null)} disabled={!!cancelingBattleId}>
              Keep battle
            </Button>
            <Button
              color="danger"
              onClick={handleCancelBattle}
              disabled={!!cancelingBattleId}
              data-cy="confirm-cancel-battle"
            >
              {cancelingBattleId ? (<><Spinner size="sm" className="me-2" /> Canceling...</>) : 'Cancel battle'}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Battle;
